/**
 * 动效常量与事件调度时序表（§11.6）。纯数据 + 纯函数，零 DOM，可直接单测。
 *
 * 为什么时长必须是 JS 常量而不是只写在 CSS 变量里：
 * 发牌 / 收池 / 推池全是 WAAPI 动画，`el.animate(frames, { duration })` 的 duration
 * 是个数字，读不到 CSS 变量。`prefersReducedMotion` 只改 CSS 变量对它们完全无效——
 * 所以 `resolveTimings()` 必须在 JS 侧把时长清零（§11.6 末尾那条硬性要求）。
 *
 * tokens.css 里的 --d-* 与这里的数值一一对应，改一处必须改两处；
 * `motion.test.ts` 里有一张对照表把两边钉死。
 */

// ============================================================================
// 缓动
// ============================================================================

/** 与 tokens.css 的 --e-* 逐字一致 */
export const EASING = {
  out: 'cubic-bezier(0.22, 0.61, 0.36, 1)',
  inOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
  spring: 'cubic-bezier(0.34, 1.32, 0.64, 1)',
  linear: 'linear',
} as const

// ============================================================================
// 主要动效参数（§11.6 动效表）
// ============================================================================

export const MOTION = {
  /** 发牌飞入：每张 220ms，stagger 55ms，translate + rotate(-14→0) + scale(.7→1) */
  deal: { duration: 220, stagger: 55, easing: EASING.out, rotate: -14, scale: 0.7 },
  /** 翻牌 3D：300ms，翻牌圈 3 张 stagger 90ms */
  flip: { duration: 300, stagger: 90, easing: EASING.inOut },
  /** 下注筹码飞出：座位 → 下注位 */
  bet: { duration: 260, easing: EASING.out, fromScale: 0.6 },
  /** 筹码飞向底池：按离池远近 stagger 40ms，末 80ms 淡出 */
  collect: { duration: 320, stagger: 40, easing: EASING.inOut, toScale: 0.72, fadeTail: 80 },
  /** 未跟注退还（原设计完全缺失的一条，§11.6 已补） */
  uncalled: { duration: 260, easing: EASING.out, toScale: 0.7 },
  /** 底池推给赢家：多赢家间隔 200ms */
  push: { duration: 420, gap: 200, easing: EASING.out, toScale: 0.8 },
  /** 筹码数字滚动（文本插值，tabular-nums 保证宽度恒定） */
  count: { duration: 600 },
  /** 行动气泡 in/hold/out */
  bubble: { in: 160, hold: 1200, out: 200 },
  /** 赢家高亮：光晕 2×600ms，只动预置元素的 opacity/scale */
  winner: { duration: 600, iterations: 2, scale: 1.08 },
  /** 成牌五张高亮：class 一次切换，不插值 filter */
  bestFive: { duration: 180 },
  /** 座位重排 FLIP */
  reseat: { duration: 200, easing: EASING.inOut },
} as const

/** 行动气泡从出现到完全消失的总时长 */
export const BUBBLE_TOTAL_MS = MOTION.bubble.in + MOTION.bubble.hold + MOTION.bubble.out

// ============================================================================
// 性能闸门（§11.6 第 5、6 条）
// ============================================================================

/**
 * 同时动画元素闸门**按类分计**：牌类 ≤8、精灵类 ≤32。
 * 原设计的「池 24 + 闸门 16」与「多人各飞 3 个精灵」直接冲突，
 * 会把收池动画整体降级成瞬移。
 */
export const GATES = { card: 8, sprite: 32 } as const

/** 筹码精灵池化 48 个 DOM 节点复用，禁止每次下注 createElement */
export const SPRITE_POOL_SIZE = 48

/** lagMs 超过它就 cancelAndFastForward() */
export const LAG_FAST_FORWARD_MS = 1500

/**
 * 一次筹码飞行派几个精灵：`clamp(1, round(log10(amount/bb)) + 1, 5)`。
 * lite 模式恒为 1。amount<=0 时不飞（返回 0），调用方据此跳过整个动画。
 */
export function spriteCountFor(amount: number, bigBlind: number, lite = false): number {
  if (!(amount > 0)) return 0
  if (lite) return 1
  const bb = Math.max(1, bigBlind)
  const n = Math.round(Math.log10(amount / bb)) + 1
  return Math.min(5, Math.max(1, n))
}

// ============================================================================
// 运动档位：reduced / lite
// ============================================================================

export interface MotionProfile {
  /** prefers-reduced-motion：时长一律 0（必须在 JS 侧生效，CSS 变量管不到 WAAPI） */
  readonly reduced: boolean
  /** 省电模式：stagger 减半、精灵 1 个、取消光晕脉冲、发牌不 rotate */
  readonly lite: boolean
}

export const FULL_MOTION: MotionProfile = { reduced: false, lite: false }

export interface ResolvedTimings {
  readonly duration: number
  readonly stagger: number
  readonly easing: string
}

/**
 * 按档位折算时长。三条规则：
 *  - reduced：duration 与 stagger 全部归零（动画等价于瞬移，但状态仍然正确）
 *  - lite：stagger 减半（总时长跟着缩短，感知上更「干脆」），duration 不动
 *  - 其余原样
 */
export function resolveTimings(
  base: { duration: number; stagger?: number; easing?: string },
  profile: MotionProfile,
): ResolvedTimings {
  const stagger = base.stagger ?? 0
  if (profile.reduced) return { duration: 0, stagger: 0, easing: EASING.linear }
  return {
    duration: base.duration,
    stagger: profile.lite ? Math.round(stagger / 2) : stagger,
    easing: base.easing ?? EASING.out,
  }
}

/** n 张牌按 stagger 依次飞入的总时长 */
export function dealTotalMs(cardCount: number, profile: MotionProfile = FULL_MOTION): number {
  if (cardCount <= 0) return 0
  const t = resolveTimings(MOTION.deal, profile)
  return (cardCount - 1) * t.stagger + t.duration
}

/** 探测本机是否应进 lite 模式（§11.6）。设置面板的手动开关优先级更高。 */
export function detectLowEndDevice(): boolean {
  const nav = globalThis.navigator as
    | (Navigator & { deviceMemory?: number; hardwareConcurrency?: number })
    | undefined
  if (nav === undefined) return false
  const cores = nav.hardwareConcurrency ?? 8
  const mem = nav.deviceMemory ?? 8
  return cores <= 4 || mem <= 4
}

/** prefers-reduced-motion 的一次性读取（订阅版见 useFxProfile） */
export function prefersReducedMotion(): boolean {
  return globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true
}

// ============================================================================
// 事件调度时序表（§11.6）
// ============================================================================

export type FxEvent =
  | { readonly kind: 'handStarted'; readonly cardCount: number }
  | { readonly kind: 'playerActed' }
  | { readonly kind: 'uncalledBetReturned' }
  | { readonly kind: 'potsUpdated' }
  | { readonly kind: 'bettingRoundEnded' }
  | { readonly kind: 'communityCards'; readonly count: number }
  | { readonly kind: 'cardsRevealed'; readonly seatCount: number }
  | { readonly kind: 'handEnded' }
  /** isHero === (seat === ownedSeat)，决定是否快进（红线 #50） */
  | { readonly kind: 'turnChanged'; readonly isHero: boolean }

/** 事件「应用前等待」 */
export function waitBeforeMs(e: FxEvent, profile: MotionProfile = FULL_MOTION): number {
  if (profile.reduced) return 0
  switch (e.kind) {
    // 收池前留一拍，否则筹码还没飞到池里数字就已经跳了
    case 'potsUpdated':
    case 'bettingRoundEnded':
      return 240
    default:
      return 0
  }
}

/** 事件「应用后保持」——队列在这段时间内不取下一个事件，动画得以播完 */
export function holdAfterMs(e: FxEvent, profile: MotionProfile = FULL_MOTION): number {
  if (profile.reduced) return 0
  switch (e.kind) {
    case 'handStarted':
      return dealTotalMs(e.cardCount, profile)
    case 'playerActed':
      return 420
    case 'uncalledBetReturned':
      return 300
    case 'communityCards':
      // 翻牌圈 3 张有 stagger，转/河单张只等一次翻牌
      return e.count >= 3 ? 540 : 300
    case 'cardsRevealed':
      return e.seatCount * 260 + 800
    case 'handEnded':
      return 420 + 600
    case 'potsUpdated':
    case 'bettingRoundEnded':
    case 'turnChanged':
      return 0
  }
}

/**
 * ★ 红线 #50：`turnChanged` 的快进**只在轮到自己时**触发。
 * 无条件快进会让 AI 连打时每个 playerActed 的 420ms 动画都被紧随的 turnChanged 吃掉，
 * 牌桌上永远看不到任何动画——这正是原设计的 bug。
 */
export function shouldFastForward(e: FxEvent): boolean {
  return e.kind === 'turnChanged' && e.isHero
}
